import {useShopperBasketsMutation, useAccessToken} from '@salesforce/commerce-sdk-react'
import {useCommerceApi} from '@salesforce/commerce-sdk-react'

// Custom Hooks
import {useCurrentBasket} from './use-current-basket'
import useBasketAssignments from './use-basket-assignments'

/**
 * Custom hook to restart a shopping basket by deleting the existing one and creating a new one with the provided order details.
 *
 * @returns {Object} An object containing the `restartBasket` function.
 *
 * @function restartBasket
 * @async
 * @param {Object} basket - The current basket object that may need to be deleted.
 * @param {Object} order - The order details used to create a new basket.
 * @param {Object} order.billingAddress - The billing address associated with the order.
 * @param {Object} order.customerInfo - Information about the customer placing the order.
 * @param {Array} order.paymentInstruments - Payment instruments used in the order.
 * @param {Array} order.productItems - Product items included in the order.
 * @param {Array} order.giftCertificateItems - Gift certificate items included in the order.
 * @param {Array} order.shipments - Shipment details for the order.
 * @param {Array} order.shippingItems - Shipping items associated with the order.
 * @returns {Promise<Object>} A promise that resolves to an object indicating the success status of the basket restart operation.
 * @property {boolean} success - Indicates whether the basket restart operation was successful.
 */
export const useRestartBasket = () => {
    const deleteBasket = useShopperBasketsMutation('deleteBasket')
    const createBasket = useShopperBasketsMutation('createBasket')
    const {data: basket, refetch} = useCurrentBasket()
    const assignBasket = useBasketAssignments()

    const {getTokenWhenReady} = useAccessToken()
    const api = useCommerceApi()
    const restartBasket = async (order) => {
        let result = {
            success: false
        }

        try {
            const {
                currency,
                customerInfo,
                productItems,
                giftCertificateItems,
                shipments,
                shippingItems,
                c_paymentCurrencyCode
            } = order
            const updatedShipments = shipments.map((shipment) => ({
                ...shipment,
                c_isAddressAndMethodSynced: true
            }))
            const updatedProductLineItems = productItems.filter(
                (product) => !product.bonusProductLineItem
            )
            // If there already is a basket, delete it to not exceed basket quota
            if (basket) {
                await refetch()
                try {
                    await deleteBasket.mutateAsync({
                        parameters: {
                            basketId: basket.basketId
                        }
                    })
                } catch (error) {
                    console.error('Error deleting basket:', error)
                }
            }

            // Create new basket
            await createBasket.mutateAsync({
                body: {
                    currency,
                    customerInfo,
                    productItems: updatedProductLineItems,
                    c_updatedGiftCertificateItems: giftCertificateItems,
                    shipments: updatedShipments,
                    shippingItems,
                    c_paymentCurrencyCode: c_paymentCurrencyCode,
                    c_isRestartBasket: true
                }
            })

            await assignBasket()
            await refetch()
            result.success = true // Set success to true if no errors occur
        } catch (error) {
            console.error('Error restarting basket:', error)
            result.success = false
        }

        return result
    }

    /**
     * Identifier representing the order ID used to restart the basket process.
     * Typically used as a constant or variable to track the current basket's order ID
     * when restarting or resetting the shopping session.
     * @type {string}
     */
    const restartBasketOrderID = async (orderID) => {
        let result = {
            success: false
        }

        try {
            const token = await getTokenWhenReady()

            const order = await api.shopperOrders.getOrder({
                parameters: {orderNo: orderID},
                headers: {
                    Authorization: `Bearer ${token}`
                }
            })

            if (order) {
                result = await restartBasket(order)
            } else {
                console.error('Order not found')
            }
        } catch (error) {
            console.error('Error fetching order or restarting basket:', error)
        }

        return result
    }

    return {restartBasket, restartBasketOrderID}
}
